import _ from "lodash";
import { ref } from "vue";
import { defineStore } from "pinia";
import { IApp, type IApiRequest } from "@/types";
import { apiURL, sendRequest } from "@/utils";
import { Env, URLs } from "@/constants";

export const useAccountStore = defineStore("account", () => {
  const accounts = ref<
    Array<IApp.IBirdEyeAccount | IApp.IGoogleAnalyticsAccount | IApp.IGoogleSearchConsoleAccount>
  >([]);
  const accountsLoadingStatus = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);
  const isCreatingOAuthURL = ref(false);

  const setAccountLoadingStatus = (value: IApp.ILoadingStatus) => {
    accountsLoadingStatus.value = value;
  };

  const fetchAccountList = async (userId: string, customerId: string) => {
    setAccountLoadingStatus(IApp.ILoadingStatus.Loading);
    const response = await sendRequest<{
      accounts: Array<
        IApp.IBirdEyeAccount | IApp.IGoogleAnalyticsAccount | IApp.IGoogleSearchConsoleAccount
      >;
    }>({
      url: apiURL(URLs.API_ACCOUNT_LIST(userId, customerId)),
      method: "GET"
    });
    const { status: responseStatus, data } = response;
    if (responseStatus) {
      accounts.value = data.accounts;
      setAccountLoadingStatus(IApp.ILoadingStatus.Success);
    } else {
      setAccountLoadingStatus(IApp.ILoadingStatus.Error);
    }
  };

  const getAccountInfo = <T extends IApp.IFeedTypes>(
    customerId: string,
    accountId: string
  ): T extends IApp.IFeedTypes.GoogleSearchConsole
    ? IApp.IGoogleSearchConsoleAccount
    : T extends IApp.IFeedTypes.BirdEye
      ? IApp.IBirdEyeAccount
      : T extends IApp.IFeedTypes.GoogleAnalytics
        ? IApp.IGoogleAnalyticsAccount
        : IApp.IAccount => {
    return _.find(accounts.value, { account: accountId, customerId: customerId }) as any;
  };

  const createOAuthURL = async (userId: string, customerId: string, feed: IApp.IFeedTypes) => {
    isCreatingOAuthURL.value = true;
    const response = await sendRequest<{ url: string }>({
      url: apiURL(URLs.API_ACCOUNT_GENERATE_GOOGLE_OAUTH_URL(userId, customerId)),
      method: "post",
      data: {
        env: "local",
        type: feed,
        redirectUrl: Env.REDIRECT_URL
      }
    });
    isCreatingOAuthURL.value = false;
    return response;
  };

  const connectGoogleAccount = async (data: {
    userId: string;
    type: IApp.IFeedTypes;
    customerId: string;
    code: string;
    name: string;
    env: IApp.IEnvironments;
  }) => {
    return await sendRequest({
      url: apiURL(URLs.API_ACCOUNT_CONNECT_ACCOUNT(data.userId, data.customerId)),
      method: "post",
      data
    });
  };

  const connectBirdEyeAccount = async (data: {
    userId: string;
    customerId: string;
    name: string;
    businessId: string;
    apiKey: string;
    env: IApp.IEnvironments;
  }) => {
    return await sendRequest<{ message: string }>({
      url: apiURL(URLs.API_ACCOUNT_CONNECT_ACCOUNT(data.userId, data.customerId)),
      method: "post",
      data: {
        feed: IApp.IFeedTypes.BirdEye,
        ...data
      }
    });
  };

  const fetchAccountInfo = async <T extends IApp.IFeedTypes>(
    userId: string,
    customerId: string,
    sourceId: string,
    key: string | number | undefined = undefined
  ) => {
    return await sendRequest<{
      account: T extends IApp.IFeedTypes.GoogleSearchConsole
        ? IApp.IGoogleSearchConsoleAccount
        : T extends IApp.IFeedTypes.BirdEye
          ? IApp.IBirdEyeAccount
          : T extends IApp.IFeedTypes.GoogleAnalytics
            ? IApp.IGoogleAnalyticsAccount
            : never;
    }>({
      url: apiURL(URLs.API_ACCOUNT_INFO(userId, customerId, sourceId)),
      method: "GET",
      params: {
        key
      }
    });
  };

  const patchAccountInfo = async <T extends IApp.IFeedTypes>(
    userId: string,
    customerId: string,
    sourceId: string,
    data: T extends IApp.IFeedTypes.GoogleSearchConsole
      ? IApiRequest.PatchGoogleSearchConsoleAccountInfo
      : T extends IApp.IFeedTypes.BirdEye
        ? IApiRequest.PatchBirdEyeAccountInfo
        : T extends IApp.IFeedTypes.GoogleAnalytics
          ? IApiRequest.PatchGoogleAnalyticsAccountInfo
          : IApiRequest.PatchAccountInfo
  ) => {
    return await sendRequest<{ message: string }>({
      url: apiURL(URLs.API_ACCOUNT_INFO_PATCH(userId, customerId, sourceId)),
      method: "PATCH",
      data
    });
  };

  const putAccountStatus = async (
    userId: string,
    customerId: string,
    accountId: string,
    status: IApp.IAccountStatus
  ) => {
    return await sendRequest<{ message: string }>({
      url: apiURL(URLs.API_ACCOUNT_STATUS_PUT(userId, customerId, accountId)),
      method: "PUT",
      data: {
        status
      }
    });
  };

  const applyAccountStatusLocally = (accountId: string, status: IApp.IAccountStatus) => {
    if (status === IApp.IAccountStatus.Deleted) {
      accounts.value = _.filter(accounts.value, (item) => item.account !== accountId);
      return;
    }
    accounts.value = _.map(accounts.value, (item) =>
      item.account === accountId ? { ...item, status } : item
    );
  };

  const softDeleteAccount = async (userId: string, customerId: string, accountId: string) => {
    const response = await putAccountStatus(
      userId,
      customerId,
      accountId,
      IApp.IAccountStatus.Deleted
    );
    if (response.status) {
      applyAccountStatusLocally(accountId, IApp.IAccountStatus.Deleted);
    }
    return response;
  };

  const archiveAccount = async (userId: string, customerId: string, accountId: string) => {
    const response = await putAccountStatus(
      userId,
      customerId,
      accountId,
      IApp.IAccountStatus.Archived
    );
    if (response.status) {
      applyAccountStatusLocally(accountId, IApp.IAccountStatus.Archived);
    }
    return response;
  };

  const pauseAccount = async (userId: string, customerId: string, accountId: string) => {
    const response = await putAccountStatus(
      userId,
      customerId,
      accountId,
      IApp.IAccountStatus.Paused
    );
    if (response.status) {
      applyAccountStatusLocally(accountId, IApp.IAccountStatus.Paused);
    }
    return response;
  };

  const resumeAccount = async (userId: string, customerId: string, accountId: string) => {
    const response = await putAccountStatus(
      userId,
      customerId,
      accountId,
      IApp.IAccountStatus.Active
    );
    if (response.status) {
      applyAccountStatusLocally(accountId, IApp.IAccountStatus.Active);
    }
    return response;
  };

  const activateAccount = async (userId: string, customerId: string, accountId: string) => {
    const response = await putAccountStatus(
      userId,
      customerId,
      accountId,
      IApp.IAccountStatus.Active
    );
    if (response.status) {
      applyAccountStatusLocally(accountId, IApp.IAccountStatus.Active);
    }
    return response;
  };

  const reloadData = async (userId: string, customerId: string, accountId: string) => {
    const response = await sendRequest<Record<string, never>>({
      url: apiURL(URLs.API_ACCOUNT_DATA_RELOAD(userId, customerId, accountId)),
      method: "POST"
    });
    return response;
  };

  return {
    accounts,
    accountsLoadingStatus,
    isCreatingOAuthURL,
    fetchAccountList,
    getAccountInfo,
    setAccountLoadingStatus,
    createOAuthURL,
    connectGoogleAccount,
    connectBirdEyeAccount,
    fetchAccountInfo,
    patchAccountInfo,
    putAccountStatus,
    softDeleteAccount,
    archiveAccount,
    pauseAccount,
    resumeAccount,
    activateAccount,
    reloadData
  };
});
